import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	FormControlLabel,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Step,
	StepLabel,
	Stepper,
	TextField,
	Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useNavigate, useParams } from 'react-router-dom';
import { validateBr } from 'js-brasil';
import AppNav from '../../components/appNav';
import { useEvent } from '../../context/EventContext';
import { ManagerContext } from '../../context/ManagerContext';
import { api } from '../../services/api';
import { maskCEP, maskCNPJ, maskCPF2, maskPassport, maskTEL } from '../../utils/mask';
import isValidEmail from '../../utils/isValidEmail';
import { isPlanAvailable } from '../../utils/plan';

const INITIAL_COMPANY_DATA = {
	document: '',
	social_reason: '',
	email: '',
	address: '',
	phone: '',
	cep: '',
	city: '',
	state: '',
	district: '',
	number: '',
	plan: '',
	quantity: 1,
	payment_form: '',
	installments: 1
};

const INITIAL_PF_DATA = {
	document: '',
	name: '',
	birth: '',
	email: '',
	gender: '',
	whatsapp: '',
	cep: '',
	street: '',
	number: '',
	neighborhood: '',
	city: '',
	state: '',
	plan: '',
	payment_form: '',
	installments: 1
};

const INITIAL_SUBSCRIBER = {
	isPassport: false,
	document: '',
	name: '',
	badge: '',
	whatsapp: '',
	email: '',
	office: '',
	birth: '',
	gender: '',
	tshirtSize: ''
};

const DEFAULT_PAYMENT_OPTIONS = ['Boleto', 'Pix', 'Transação ou Depósito Bancário', 'Nota de empenho/depósito bancário '];

const COMPANY_STEPS = ['Dados da Instituição', 'Tipo de Inscrição', 'Inscritos', 'Pagamento', 'Revisão'];
const PF_STEPS = ['Dados Pessoais', 'Tipo de Inscrição', 'Pagamento', 'Revisão'];

function formatCurrency(value) {
	return Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(value || 0));
}

function createSubscribers(quantity, existing = []) {
	const target = Math.max(1, Number(quantity || 1));
	const next = [];
	for (let i = 0; i < target; i += 1) {
		next.push(existing[i] ? { ...INITIAL_SUBSCRIBER, ...existing[i] } : { ...INITIAL_SUBSCRIBER });
	}
	return next;
}

function normalizeBirth(value) {
	if (!value) return '';
	if (value.includes('T')) return value.split('T')[0];
	return value;
}

export default function AutoSubscribe() {
	const navigate = useNavigate();
	const { eventId } = useParams();
	const { events, setEvents } = useEvent();
	const { manager } = useContext(ManagerContext);

	const [eventLoading, setEventLoading] = useState(true);
	const [loading, setLoading] = useState(false);
	const [success, setSuccess] = useState(false);
	const [stepError, setStepError] = useState('');

	const [payerType, setPayerType] = useState('INSTITUICAO');
	const [activeStep, setActiveStep] = useState(0);
	const [companyData, setCompanyData] = useState(INITIAL_COMPANY_DATA);
	const [pfData, setPfData] = useState(INITIAL_PF_DATA);
	const [subscribers, setSubscribers] = useState(createSubscribers(1));

	const [companyLoading, setCompanyLoading] = useState(false);
	const [pfLoading, setPfLoading] = useState(false);
	const [acceptPolicy, setAcceptPolicy] = useState(false);
	const [openBoletoModal, setOpenBoletoModal] = useState(false);
	const [boletoGroups, setBoletoGroups] = useState([]);
	const [lastPaymentForm, setLastPaymentForm] = useState('');

	const [openPixModal, setOpenPixModal] = useState(false);
	const [secondViaDocument, setSecondViaDocument] = useState('');
	const [secondViaLoading, setSecondViaLoading] = useState(false);
	const [secondViaError, setSecondViaError] = useState('');
	const [secondViaMode, setSecondViaMode] = useState(''); // 'boleto' | 'pix'
	const [openReceiptModal, setOpenReceiptModal] = useState(false);
	const [receiptDocument, setReceiptDocument] = useState('');
	const [receiptLoading, setReceiptLoading] = useState(false);
	const [receiptError, setReceiptError] = useState('');

	useEffect(() => {
		async function getEventById() {
			if (!eventId) {
				setStepError('Evento não informado');
				setEventLoading(false);
				return;
			}

			try {
				const { data } = await api.get(`/events/${eventId}?populate=plans`);
				if (!data?.isActive) {
					setStepError('Evento desativado');
					setTimeout(() => navigate(`/event/${eventId}`), 1500);
					return;
				}
				setEvents(data);
			} catch (error) {
				setStepError('Erro ao carregar dados do evento');
			} finally {
				setEventLoading(false);
			}
		}

		getEventById();

		return () => {
			// Evita quebrar a Home (Events), que espera `events` como array no contexto global.
			setEvents(prev => (Array.isArray(prev) ? prev : []));
		};
	}, [eventId, navigate, setEvents]);

	const currentSteps = payerType === 'INSTITUICAO' ? COMPANY_STEPS : PF_STEPS;
	const lastStep = currentSteps.length - 1;
	const maxInstallments = events?.isInstallments ? 2 : 1;
	const needsPolicyAcceptance = manager?.fantasy === 'APEPREV' || manager?.fantasy === 'AGIP';
	const basePath = useMemo(() => {
		const first = window.location.pathname.split('/').filter(Boolean)[0];
		return ['apeprev', 'semper'].includes(first) ? `/${first}` : '';
	}, []);

	const plans = useMemo(() => events?.plans || [], [events]);
	const selectedCompanyPlan = useMemo(
		() => plans.find(plan => String(plan.id) === String(companyData.plan)),
		[plans, companyData.plan]
	);
	const selectedPfPlan = useMemo(
		() => plans.find(plan => String(plan.id) === String(pfData.plan)),
		[plans, pfData.plan]
	);
	const companyTotal = useMemo(
		() => Number(selectedCompanyPlan?.price || 0) * Number(companyData.quantity || 1),
		[selectedCompanyPlan, companyData.quantity]
	);
	const pfTotal = useMemo(
		() => Number(selectedPfPlan?.price || 0),
		[selectedPfPlan]
	);
	const companyIsFree = companyTotal === 0 && !!companyData.plan;
	const pfIsFree = pfTotal === 0 && !!pfData.plan;

	const paymentOptions = useMemo(() => {
		if (events?.payment_options) {
			return Object.keys(events.payment_options).filter(key => events.payment_options[key]);
		}
		return DEFAULT_PAYMENT_OPTIONS;
	}, [events]);

	const currentPaymentOptions = payerType === 'INSTITUICAO'
		? (companyIsFree ? ['Gratuito'] : paymentOptions)
		: (pfIsFree ? ['Gratuito'] : paymentOptions);

	const installmentOptions = useMemo(() => {
		const total = payerType === 'INSTITUICAO' ? companyTotal : pfTotal;
		const qty = payerType === 'INSTITUICAO' ? Math.max(1, Number(companyData.quantity || 1)) : 1;
		const max = Math.max(1, maxInstallments);
		const result = [];
		for (let i = 1; i <= max; i += 1) {
			const installmentValue = total / i;
			const perSubscriberValue = installmentValue / qty;
			const label = qty > 1
				? `${i}x de ${formatCurrency(perSubscriberValue)} por inscrito`
				: `${i}x de ${formatCurrency(installmentValue)}`;
			result.push({ value: i, label });
		}
		return result;
	}, [payerType, companyTotal, pfTotal, maxInstallments, companyData.quantity]);

	const selectedCompanyInstallmentValue = useMemo(() => {
		const installments = Math.max(1, Number(companyData.installments || 1));
		return companyTotal / installments;
	}, [companyTotal, companyData.installments]);

	const selectedCompanyPerSubscriberInstallment = useMemo(() => {
		const qty = Math.max(1, Number(companyData.quantity || 1));
		return selectedCompanyInstallmentValue / qty;
	}, [selectedCompanyInstallmentValue, companyData.quantity]);

	function resetToType(type) {
		setPayerType(type);
		setActiveStep(0);
		setStepError('');
		setSuccess(false);
		setAcceptPolicy(false);
		setCompanyData(INITIAL_COMPANY_DATA);
		setPfData(INITIAL_PF_DATA);
		setSubscribers(createSubscribers(1));
	}

	function startNewSubscription() {
		setSuccess(false);
		setStepError('');
		setAcceptPolicy(false);
		setLastPaymentForm('');
		setBoletoGroups([]);
		setOpenBoletoModal(false);
		setActiveStep(0);
		setCompanyData({ ...INITIAL_COMPANY_DATA });
		setPfData({ ...INITIAL_PF_DATA });
		setSubscribers(createSubscribers(1));
	}

	async function handleCompanyCNPJBlur() {
		const cnpj = companyData.document;
		if (!validateBr.cnpj(cnpj)) return;
		setCompanyLoading(true);
		try {
			const encodedCnpj = cnpj.replace('/', '%2F');
			const { data } = await api.get(`/companies/${encodedCnpj}?event=${events?.id || eventId}`);
			if (data) {
				setCompanyData(prev => ({
					...prev,
					...data,
					document: cnpj,
					cep: data.cep || '',
					address: data.address || '',
					number: data.number || '',
					district: data.district || '',
					city: data.city || '',
					state: data.state || '',
					plan: prev.plan,
					quantity: prev.quantity,
					payment_form: prev.payment_form,
					installments: prev.installments
				}));
			}
		} catch (error) {
			setCompanyData(prev => ({ ...prev, document: cnpj }));
		} finally {
			setCompanyLoading(false);
		}
	}

	async function handlePfCPFBlur() {
		const cpf = pfData.document;
		if (!validateBr.cpf(cpf)) return;
		setPfLoading(true);
		try {
			const { data } = await api.post('/subscriber/form', { data: { cpf } });
			if (data) {
				setPfData(prev => ({
					...prev,
					...data,
					document: maskCPF2(cpf),
					birth: normalizeBirth(data.birth || prev.birth),
					cep: data.cep || '',
					street: data.street || '',
					number: data.number || '',
					neighborhood: data.neighborhood || '',
					city: data.city || '',
					state: data.state || '',
					plan: prev.plan,
					payment_form: prev.payment_form,
					installments: prev.installments
				}));
			}
		} catch (error) {
			setPfData(prev => ({ ...prev, document: maskCPF2(cpf) }));
		} finally {
			setPfLoading(false);
		}
	}

	async function handleCepBlurPessoaFisica() {
		const cep = pfData.cep.replace(/\D/g, '');
		if (cep.length !== 8) return;
		setPfLoading(true);
		try {
			const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
			const data = await response.json();
			if (!data.erro) {
				setPfData(prev => ({
					...prev,
					street: data.logradouro,
					neighborhood: data.bairro,
					city: data.localidade,
					state: data.uf
				}));
			}
		} catch (error) {
			return;
		} finally {
			setPfLoading(false);
		}
	}

	async function handleCepBlurInstituicao() {
		const cep = companyData.cep.replace(/\D/g, '');
		if (cep.length !== 8) return;
		setCompanyLoading(true);
		try {
			const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
			const data = await response.json();
			if (!data.erro) {
				setCompanyData(prev => ({
					...prev,
					address: data.logradouro,
					district: data.bairro,
					city: data.localidade,
					state: data.uf
				}));
			}
		} catch (error) {
			return;
		} finally {
			setCompanyLoading(false);
		}
	}

	function handleOpenSecondViaModal(mode) {
		setSecondViaMode(mode);
		setSecondViaDocument('');
		setSecondViaError('');
		setOpenPixModal(true);
	}

	function handleCloseSecondViaModal() {
		setOpenPixModal(false);
		setSecondViaDocument('');
		setSecondViaError('');
		setSecondViaLoading(false);
		setSecondViaMode('');
	}

	async function handleBuscarSecondVia() {
		const isCompany = payerType === 'INSTITUICAO';
		const isValid = isCompany ? validateBr.cnpj(secondViaDocument) : validateBr.cpf(secondViaDocument);

		if (!isValid) {
			setSecondViaError(isCompany ? 'CNPJ inválido' : 'CPF inválido');
			return;
		}

		setSecondViaLoading(true);
		setSecondViaError('');

		try {
			const documento = secondViaDocument.replace('/', '%2F');
			const { data } = await api.get(`/order/individual/${documento}/${eventId}`);

			if (data && data.payment_url) {
				window.open(data.payment_url, '_blank');
				handleCloseSecondViaModal();
			} else {
				setSecondViaError('Nenhum pagamento pendente encontrado para este documento.');
			}
		} catch (error) {
			console.error('Erro ao buscar segunda via:', error);
			setSecondViaError(error.response?.data?.error?.message || 'Erro ao buscar pagamento. Tente novamente.');
		} finally {
			setSecondViaLoading(false);
		}
	}

	function handleOpenReceiptModal() {
		setReceiptDocument('');
		setReceiptError('');
		setOpenReceiptModal(true);
	}

	function handleCloseReceiptModal() {
		setOpenReceiptModal(false);
		setReceiptDocument('');
		setReceiptError('');
		setReceiptLoading(false);
	}

	async function handleBuscarReceipt() {
		if (!validateBr.cpf(receiptDocument)) {
			setReceiptError('CPF inválido');
			return;
		}

		setReceiptLoading(true);
		setReceiptError('');
		try {
			const { data } = await api.get(`/order/individual/${receiptDocument}/${eventId}`);
			const externalId = data?.external_id || data?.externalId || data?.transaction || data?.payment_url?.split('/').filter(Boolean).pop();

			console.log('External ID encontrado:', externalId);
			if (!externalId) {
				setReceiptError('Nenhuma inscrição encontrada para este CPF neste evento.');
				return;
			}
			handleCloseReceiptModal();
			navigate(`/recibo/${externalId}`);
		} catch (error) {
			setReceiptError(error.response?.data?.error?.message || 'Nenhuma inscrição encontrada para este CPF neste evento.');
		} finally {
			setReceiptLoading(false);
		}
	}

	async function handleSubscriberDocumentBlur(index) {
		const current = subscribers[index];
		if (current.isPassport || !validateBr.cpf(current.document)) return;
		try {
			const { data } = await api.post('/subscriber/form', { data: { cpf: current.document } });
			if (data) {
				setSubscribers(prev => {
					const next = [...prev];
					next[index] = {
						...next[index],
						name: data.name || next[index].name,
						badge: data.badge || next[index].badge,
						email: data.email || next[index].email,
						birth: normalizeBirth(data.birth || next[index].birth),
						gender: data.gender || next[index].gender,
						whatsapp: data.whatsapp || next[index].whatsapp,
						office: data.office || next[index].office,
						tshirtSize: data.tshirtSize || next[index].tshirtSize
					};
					return next;
				});
			}
		} catch (error) {
			return;
		}
	}

	function validateSubscriberRows() {
		const docs = new Set();
		const names = new Set();
		const whats = new Set();

		for (let i = 0; i < subscribers.length; i += 1) {
			const sub = subscribers[i];
			const row = i + 1;
			const docNorm = sub.document.replace(/\W/g, '').toLowerCase();
			const nameNorm = sub.name.trim().toLowerCase();
			const whatsNorm = sub.whatsapp.replace(/\D/g, '');

			if (!sub.document) return `Inscrito ${row}: documento obrigatório`;
			if (!sub.isPassport && !validateBr.cpf(sub.document)) return `Inscrito ${row}: CPF inválido`;
			if (sub.isPassport && sub.document.length < 6) return `Inscrito ${row}: passaporte inválido`;
			if (!sub.name || sub.name.trim().length < 8) return `Inscrito ${row}: nome inválido`;
			if (sub.badge && sub.badge.trim().length < 3) return `Inscrito ${row}: nome de crachá inválido`;
			if (!sub.email || !isValidEmail(sub.email)) return `Inscrito ${row}: e-mail inválido`;
			if (!sub.birth) return `Inscrito ${row}: nascimento obrigatório`;
			if (!sub.gender) return `Inscrito ${row}: gênero obrigatório`;
			if (!sub.whatsapp || sub.whatsapp.replace(/\D/g, '').length < 10) return `Inscrito ${row}: WhatsApp inválido`;
			if (!sub.office || sub.office.trim().length < 4) return `Inscrito ${row}: cargo/função inválido`;
			if (manager?.config_path !== 'agip' && basePath !== '/semper' && !sub.tshirtSize) return `Inscrito ${row}: tamanho de camiseta obrigatório`;

			if (docs.has(docNorm)) return `Documento repetido entre inscritos`;
			if (names.has(nameNorm)) return `Nome repetido entre inscritos`;
			if (whats.has(whatsNorm)) return `WhatsApp repetido entre inscritos`;

			docs.add(docNorm);
			names.add(nameNorm);
			whats.add(whatsNorm);
		}

		return '';
	}

	function validateCurrentStep() {
		setStepError('');

		if (payerType === 'INSTITUICAO') {
			if (activeStep === 0) {
				if (!companyData.document || !validateBr.cnpj(companyData.document)) return 'CNPJ obrigatório e válido';
				if (!companyData.social_reason) return 'Razão social obrigatória';
				if (!companyData.email || !isValidEmail(companyData.email)) return 'E-mail obrigatório e válido';
				if (!companyData.phone || companyData.phone.replace(/\D/g, '').length < 10) return 'Telefone obrigatório e válido';
				if (!companyData.cep || companyData.cep.replace(/\D/g, '').length !== 8) return 'CEP obrigatório e válido';
				if (!companyData.address) return 'Endereço obrigatório';
				if (!companyData.number) return 'Número obrigatório';
				if (!companyData.district) return 'Bairro obrigatório';
				if (!companyData.city) return 'Cidade obrigatória';
				if (!companyData.state) return 'Estado obrigatório';
				return '';
			}
			if (activeStep === 1) {
				if (!companyData.plan) return 'Selecione um tipo de inscrição';
				if (!companyData.quantity || Number(companyData.quantity) < 1) return 'Quantidade obrigatória';
				return '';
			}
			if (activeStep === 2) {
				return validateSubscriberRows();
			}
			if (activeStep === 3) {
				if (!companyData.payment_form) return 'Forma de pagamento obrigatória';
				if (!companyData.installments || Number(companyData.installments) < 1) return 'Parcelamento obrigatório';
				return '';
			}
			if (activeStep === 4) {
				if (needsPolicyAcceptance && !acceptPolicy) return 'Você precisa aceitar os termos para continuar';
				return '';
			}
		}

		if (activeStep === 0) {
			if (!pfData.document || !validateBr.cpf(pfData.document)) return 'CPF obrigatório e válido';
			if (!pfData.name || pfData.name.trim().length < 4) return 'Nome obrigatório';
			if (!pfData.birth) return 'Nascimento obrigatório';
			if (!pfData.gender) return 'Gênero obrigatório';
			if (!pfData.whatsapp || pfData.whatsapp.replace(/\D/g, '').length < 10) return 'WhatsApp obrigatório e válido';
			if (!pfData.cep || pfData.cep.replace(/\D/g, '').length !== 8) return 'CEP obrigatório e válido';
			if (!pfData.street) return 'Rua obrigatória';
			if (!pfData.number) return 'Número obrigatório';
			if (!pfData.neighborhood) return 'Bairro obrigatório';
			if (!pfData.city) return 'Cidade obrigatória';
			if (!pfData.state) return 'Estado obrigatório';
			if (pfData.email && !isValidEmail(pfData.email)) return 'E-mail inválido';
			return '';
		}
		if (activeStep === 1) {
			if (!pfData.plan) return 'Selecione um tipo de inscrição';
			return '';
		}
		if (activeStep === 2) {
			if (!pfData.payment_form) return 'Forma de pagamento obrigatória';
			if (!pfData.installments || Number(pfData.installments) < 1) return 'Parcelamento obrigatório';
			return '';
		}
		if (activeStep === 3) {
			if (needsPolicyAcceptance && !acceptPolicy) return 'Você precisa aceitar os termos para continuar';
			return '';
		}

		return '';
	}

	function buildCompanyPayload() {
		const installments = Number(companyData.installments || 1);
		return {
			event: eventId,
			company: {
				document: companyData.document,
				social_reason: companyData.social_reason,
				email: companyData.email,
				address: companyData.address,
				phone: companyData.phone,
				cep: companyData.cep,
				city: companyData.city,
				state: companyData.state,
				district: companyData.district,
				number: companyData.number
			},
			plan: Number(companyData.plan),
			plan_price: Number(selectedCompanyPlan?.price || 0),
			quantity: Number(companyData.quantity || 1),
			payment_form: companyIsFree ? 'Gratuito' : companyData.payment_form,
			installments,
			hosting: 'NoHosting',
			daily: 0,
			subscribers: subscribers.map(sub => ({
				isPassport: Boolean(sub.isPassport),
				document: sub.document,
				name: sub.name,
				badge: sub.badge,
				whatsapp: sub.whatsapp,
				email: sub.email,
				office: sub.office,
				birth: sub.birth,
				gender: sub.gender,
				tshirtSize: sub.tshirtSize
			}))
		};
	}

	function buildPfPayload() {
		const installments = Number(pfData.installments || 1);
		return {
			document: pfData.document,
			name: pfData.name,
			birth: pfData.birth,
			email: pfData.email,
			gender: pfData.gender,
			whatsapp: pfData.whatsapp,
			cep: pfData.cep,
			street: pfData.street,
			number: pfData.number,
			neighborhood: pfData.neighborhood,
			city: pfData.city,
			state: pfData.state,
			plan: Number(pfData.plan),
			payment_form: pfIsFree ? 'Gratuito' : pfData.payment_form,
			installments,
			event: eventId
		};
	}

	function buildBoletoGroups(payments) {
		if (!Array.isArray(payments) || payments.length === 0) return [];

		const installmentsCount = Math.max(1, Number(
			payerType === 'INSTITUICAO' ? companyData.installments : pfData.installments
		));

		if (payerType === 'PF') {
			return [
				{
					name: pfData.name || 'Inscrito',
					boletos: payments.map((item, index) => ({
						parcel: Number(item.installment || index + 1),
						externalId: item.external_id,
						url: item.payment_url,
						status: item.status,
						total: item.total
					}))
				}
			];
		}

		const groupsMap = new Map();
		payments.forEach((item, index) => {
			const fallbackIndex = Math.floor(index / installmentsCount);
			const fallbackName = subscribers[fallbackIndex]?.name || `Inscrito ${fallbackIndex + 1}`;
			const name = item?.subscriber?.name || item?.name || fallbackName;

			if (!groupsMap.has(name)) groupsMap.set(name, []);
			groupsMap.get(name).push({
				parcel: Number(item.installment || ((index % installmentsCount) + 1)),
				externalId: item.external_id,
				url: item.payment_url,
				status: item.status,
				total: item.total
			});
		});

		return Array.from(groupsMap.entries()).map(([name, boletos]) => ({
			name,
			boletos: boletos.sort((a, b) => a.parcel - b.parcel)
		}));
	}

	async function handleSubmit() {
		setStepError('');
		setLoading(true);
		setSuccess(false);
		try {
			if (payerType === 'INSTITUICAO') {
				const { data } = await api.post('/order/installment', { data: buildCompanyPayload() });
				if (Array.isArray(data) && data.length > 0) {
					setBoletoGroups(buildBoletoGroups(data));
					setOpenBoletoModal(true);
				} else if (data?.payment_url) {
					window.open(data.payment_url, '_blank');
				}
			} else {
				const { data } = await api.post('/order/installment', { data: buildPfPayload() });
				if (Array.isArray(data) && data.length > 0) {
					setBoletoGroups(buildBoletoGroups(data));
					setOpenBoletoModal(true);
				} else if (data?.payment_url) {
					window.open(data.payment_url, '_blank');
				}
			}
				// Capture chosen forma de pagamento antes de resetar os dados
				const chosen = payerType === 'INSTITUICAO' ? companyData.payment_form : pfData.payment_form;
				setLastPaymentForm(chosen || '');
				setSuccess(true);
			setStepError('');
			setAcceptPolicy(false);
				setActiveStep(lastStep);
		} catch (error) {
			setStepError(error.response?.data?.error?.message || error.message || 'Erro ao realizar inscrição');
		} finally {
			setLoading(false);
		}
	}

	function handleNext() {
		const err = validateCurrentStep();
		if (err) {
			setStepError(err);
			return;
		}
		setStepError('');
		setActiveStep(prev => Math.min(prev + 1, lastStep));
	}

	function handleBack() {
		setStepError('');
		setActiveStep(prev => Math.max(prev - 1, 0));
	}

	function handleCompanyQuantityChange(value) {
		const quantity = Math.max(1, Number(value || 1));
		setCompanyData(prev => ({ ...prev, quantity }));
		setSubscribers(prev => createSubscribers(quantity, prev));
	}

	function updateSubscriber(index, field, value) {
		setSubscribers(prev => {
			const next = [...prev];
			next[index] = { ...next[index], [field]: value };
			return next;
		});
	}

	function renderCompanyStepContent() {
		if (activeStep === 0) {
			return (
				<Grid container spacing={2}>
					<Grid item xs={12} sm={4}>
						<TextField
							label="CNPJ"
							fullWidth
							value={companyData.document}
							onChange={e => setCompanyData({ ...companyData, document: maskCNPJ(e.target.value) })}
							onBlur={handleCompanyCNPJBlur}
							error={!!companyData.document && !validateBr.cnpj(companyData.document)}
							helperText={companyData.document && !validateBr.cnpj(companyData.document) ? 'CNPJ inválido' : 'Digite o CNPJ e saia do campo para buscar'}
							InputProps={{ endAdornment: companyLoading && <CircularProgress size={20} /> }}
							inputProps={{ maxLength: 18 }}
						/>
					</Grid>
					<Grid item xs={12} sm={8}>
						<TextField label="Razão Social" fullWidth value={companyData.social_reason} onChange={e => setCompanyData({ ...companyData, social_reason: e.target.value })} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField label="E-mail" fullWidth value={companyData.email} onChange={e => setCompanyData({ ...companyData, email: e.target.value })} />
					</Grid>
					<Grid item xs={12} sm={6}>
						<TextField label="Telefone" fullWidth value={companyData.phone} onChange={e => setCompanyData({ ...companyData, phone: maskTEL(e.target.value) })} inputProps={{ maxLength: 15 }} />
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField label="CEP" fullWidth value={companyData.cep} onChange={e => setCompanyData({ ...companyData, cep: maskCEP(e.target.value) })} onBlur={handleCepBlurInstituicao} inputProps={{ maxLength: 9 }} />
					</Grid>
					<Grid item xs={12} sm={5}>
						<TextField label="Endereço" fullWidth value={companyData.address} onChange={e => setCompanyData({ ...companyData, address: e.target.value })} />
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField label="Número" fullWidth value={companyData.number} onChange={e => setCompanyData({ ...companyData, number: e.target.value })} />
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField label="Bairro" fullWidth value={companyData.district} onChange={e => setCompanyData({ ...companyData, district: e.target.value })} />
					</Grid>
					<Grid item xs={12} sm={3}>
						<TextField label="Cidade" fullWidth value={companyData.city} onChange={e => setCompanyData({ ...companyData, city: e.target.value })} />
					</Grid>
					<Grid item xs={12} sm={2}>
						<TextField label="Estado" fullWidth value={companyData.state} onChange={e => setCompanyData({ ...companyData, state: e.target.value })} />
					</Grid>
				</Grid>
			);
		}

		if (activeStep === 1) {
			return (
				<Grid container spacing={2}>
					<Grid item xs={12} sm={8}>
						<FormControl fullWidth>
							<InputLabel id="company-plan-label">Tipo de Inscrição</InputLabel>
							<Select
								labelId="company-plan-label"
								value={companyData.plan}
								label="Tipo de Inscrição"
								onChange={e => {
									const selected = plans.find(p => String(p.id) === String(e.target.value));
									const isFree = Number(selected?.price || 0) === 0;
									setCompanyData(prev => ({
										...prev,
										plan: e.target.value,
										payment_form: isFree ? 'Gratuito' : (prev.payment_form === 'Gratuito' ? '' : prev.payment_form),
										installments: 1
									}));
								}}
							>
								<MenuItem value="">Selecione</MenuItem>
								{plans.filter(plan => isPlanAvailable(plan, { requireActive: true })).map(plan => (
									<MenuItem key={plan.id} value={plan.id}>
										{plan.type} - {formatCurrency(plan.price)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} sm={4}>
						<TextField
							label="Quantidade"
							type="number"
							fullWidth
							value={companyData.quantity}
							onChange={e => handleCompanyQuantityChange(e.target.value)}
							inputProps={{ min: 1 }}
						/>
					</Grid>
				</Grid>
			);
		}

		if (activeStep === 2) {
			return (
				<Box>
					<Typography variant="h6" sx={{ mb: 2 }}>Inscritos ({companyData.quantity})</Typography>
					<Grid container spacing={2}>
						{subscribers.map((sub, index) => (
							<Grid item xs={12} key={`subscriber-${index + 1}`}>
								<Paper variant="outlined" sx={{ p: 2 }}>
									<Typography variant="subtitle2" sx={{ mb: 2 }}>Inscrito {index + 1}</Typography>
									<Grid container spacing={2}>
										<Grid item xs={12}>
											<FormControlLabel
												control={
													<Checkbox
														checked={sub.isPassport}
														onChange={e => {
															updateSubscriber(index, 'isPassport', e.target.checked);
															updateSubscriber(index, 'document', '');
														}}
													/>
												}
												label="Estrangeiro (usar passaporte)"
											/>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField
												label={sub.isPassport ? 'Passaporte' : 'CPF'}
												fullWidth
												value={sub.document}
												onChange={e => updateSubscriber(index, 'document', sub.isPassport ? maskPassport(e.target.value) : maskCPF2(e.target.value))}
												onBlur={() => handleSubscriberDocumentBlur(index)}
												inputProps={{ maxLength: sub.isPassport ? 20 : 14 }}
											/>
										</Grid>
										<Grid item xs={12} md={3}>
											<TextField label="Nome Completo" fullWidth value={sub.name} onChange={e => updateSubscriber(index, 'name', e.target.value)} />
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField label="Nome p/ Crachá" fullWidth value={sub.badge} onChange={e => updateSubscriber(index, 'badge', e.target.value)} />
										</Grid>
										<Grid item xs={12} md={3}>
											<TextField label="E-mail" fullWidth value={sub.email} onChange={e => updateSubscriber(index, 'email', e.target.value)} />
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField label="Nascimento" type="date" fullWidth value={sub.birth} onChange={e => updateSubscriber(index, 'birth', e.target.value)} InputLabelProps={{ shrink: true }} />
										</Grid>
										<Grid item xs={12} md={2}>
											<FormControl fullWidth>
												<InputLabel id={`gender-label-${index}`}>Gênero</InputLabel>
												<Select
													labelId={`gender-label-${index}`}
													value={sub.gender}
													label="Gênero"
													onChange={e => updateSubscriber(index, 'gender', e.target.value)}
												>
													<MenuItem value="">Selecione</MenuItem>
													<MenuItem value="M">Masculino</MenuItem>
													<MenuItem value="F">Feminino</MenuItem>
													<MenuItem value="Outro">Outro</MenuItem>
												</Select>
											</FormControl>
										</Grid>
										<Grid item xs={12} md={2}>
											<TextField label="WhatsApp" fullWidth value={sub.whatsapp} onChange={e => updateSubscriber(index, 'whatsapp', maskTEL(e.target.value))} inputProps={{ maxLength: 15 }} />
										</Grid>
										<Grid item xs={12} md={3}>
											<TextField label="Cargo/Função" fullWidth value={sub.office} onChange={e => updateSubscriber(index, 'office', e.target.value)} />
										</Grid>
										{(basePath !== '/semper' && manager?.config_path !== 'agip') && (
											<Grid item xs={12} md={2}>
												<FormControl fullWidth>
													<InputLabel id={`shirt-label-${index}`}>Camiseta</InputLabel>
													<Select
														labelId={`shirt-label-${index}`}
														value={sub.tshirtSize}
														label="Camiseta"
														onChange={e => updateSubscriber(index, 'tshirtSize', e.target.value)}
													>
														<MenuItem value="">Selecione</MenuItem>
														<MenuItem value="P">P</MenuItem>
														<MenuItem value="M">M</MenuItem>
														<MenuItem value="G">G</MenuItem>
														<MenuItem value="GG">GG</MenuItem>
														<MenuItem value="EG">EG</MenuItem>
													</Select>
												</FormControl>
											</Grid>
										)}
									</Grid>
								</Paper>
							</Grid>
						))}
					</Grid>
				</Box>
			);
		}

		if (activeStep === 3) {
			return (
				<Grid container spacing={2}>
					<Grid item xs={12} sm={events?.isInstallments ? 6 : 12}>
						<FormControl fullWidth>
							<InputLabel id="company-payment-form-label">Forma de Pagamento</InputLabel>
							<Select
								labelId="company-payment-form-label"
								value={companyData.payment_form}
								label="Forma de Pagamento"
								onChange={e => setCompanyData(prev => ({ ...prev, payment_form: e.target.value, installments: 1 }))}
							>
								<MenuItem value="">Selecione</MenuItem>
								{currentPaymentOptions.map(option => (
									<MenuItem key={option} value={option}>{option}</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					{events?.isInstallments && (
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth>
								<InputLabel id="company-installments-label">Parcelamento</InputLabel>
								<Select
									labelId="company-installments-label"
									value={companyData.installments}
									label="Parcelamento"
									onChange={e => setCompanyData(prev => ({ ...prev, installments: Number(e.target.value) }))}
									disabled={!companyData.payment_form || companyData.payment_form === 'Gratuito'}
								>
									{installmentOptions.map(option => (
										<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					)}
					<Grid item xs={12}>
						<Alert severity="info">Valor total: {formatCurrency(companyTotal)}</Alert>
					</Grid>
					{Number(companyData.quantity || 1) > 1 && (
						<Grid item xs={12}>
							<Alert severity="info">
								Parcelamento selecionado: {Number(companyData.installments || 1)}x de {formatCurrency(selectedCompanyInstallmentValue)}.
								Valor por inscrito em cada parcela: {formatCurrency(selectedCompanyPerSubscriberInstallment)}.
							</Alert>
						</Grid>
					)}
				</Grid>
			);
		}

		return renderReview();
	}

	function renderPfStepContent() {
		if (activeStep === 0) {
			return (
				<Grid container spacing={2}>
					<Grid item xs={12} md={4}>
						<TextField
							label="CPF"
							fullWidth
							value={pfData.document}
							onChange={e => setPfData({ ...pfData, document: maskCPF2(e.target.value) })}
							onBlur={handlePfCPFBlur}
							error={!!pfData.document && !validateBr.cpf(pfData.document)}
							helperText={pfData.document && !validateBr.cpf(pfData.document) ? 'CPF inválido' : 'Digite o CPF e saia do campo para buscar'}
							InputProps={{ endAdornment: pfLoading && <CircularProgress size={20} /> }}
							inputProps={{ maxLength: 14 }}
						/>
					</Grid>
					<Grid item xs={12} md={8}>
						<TextField label="Nome Completo" fullWidth value={pfData.name} onChange={e => setPfData({ ...pfData, name: e.target.value })} />
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField label="Nascimento" type="date" fullWidth value={pfData.birth} onChange={e => setPfData({ ...pfData, birth: e.target.value })} InputLabelProps={{ shrink: true }} />
					</Grid>
					<Grid item xs={12} md={3}>
						<FormControl fullWidth>
							<InputLabel id="pf-gender-label">Gênero</InputLabel>
							<Select
								labelId="pf-gender-label"
								value={pfData.gender}
								label="Gênero"
								onChange={e => setPfData({ ...pfData, gender: e.target.value })}
							>
								<MenuItem value="">Selecione</MenuItem>
								<MenuItem value="M">Masculino</MenuItem>
								<MenuItem value="F">Feminino</MenuItem>
								<MenuItem value="S">Não informar</MenuItem>
								<MenuItem value="Outros">Outros</MenuItem>
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField label="WhatsApp" fullWidth value={pfData.whatsapp} onChange={e => setPfData({ ...pfData, whatsapp: maskTEL(e.target.value) })} inputProps={{ maxLength: 15 }} />
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField label="E-mail (opcional)" fullWidth value={pfData.email} onChange={e => setPfData({ ...pfData, email: e.target.value })} />
					</Grid>
					<Grid item xs={12} md={3}>
						<TextField label="CEP" fullWidth value={pfData.cep} onChange={e => setPfData({ ...pfData, cep: maskCEP(e.target.value) })} onBlur={handleCepBlurPessoaFisica} inputProps={{ maxLength: 9 }} />
					</Grid>
					<Grid item xs={12} md={5}>
						<TextField label="Rua" fullWidth value={pfData.street} onChange={e => setPfData({ ...pfData, street: e.target.value })} />
					</Grid>
					<Grid item xs={12} md={2}>
						<TextField label="Número" fullWidth value={pfData.number} onChange={e => setPfData({ ...pfData, number: e.target.value })} />
					</Grid>
					<Grid item xs={12} md={4}>
						<TextField label="Bairro" fullWidth value={pfData.neighborhood} onChange={e => setPfData({ ...pfData, neighborhood: e.target.value })} />
					</Grid>
					<Grid item xs={12} md={4}>
						<TextField label="Cidade" fullWidth value={pfData.city} onChange={e => setPfData({ ...pfData, city: e.target.value })} />
					</Grid>
					<Grid item xs={12} md={2}>
						<TextField label="Estado" fullWidth value={pfData.state} onChange={e => setPfData({ ...pfData, state: e.target.value })} />
					</Grid>
				</Grid>
			);
		}

		if (activeStep === 1) {
			return (
				<Grid container spacing={2}>
					<Grid item xs={12}>
						<FormControl fullWidth>
							<InputLabel id="pf-plan-label">Tipo de Inscrição</InputLabel>
							<Select
								labelId="pf-plan-label"
								value={pfData.plan}
								label="Tipo de Inscrição"
								onChange={e => {
									const selected = plans.find(p => String(p.id) === String(e.target.value));
									const isFree = Number(selected?.price || 0) === 0;
									setPfData(prev => ({
										...prev,
										plan: e.target.value,
										payment_form: isFree ? 'Gratuito' : (prev.payment_form === 'Gratuito' ? '' : prev.payment_form),
										installments: 1
									}));
								}}
							>
								<MenuItem value="">Selecione</MenuItem>
								{plans.filter(plan => isPlanAvailable(plan, { requireActive: true })).map(plan => (
									<MenuItem key={plan.id} value={plan.id}>
										{plan.type} - {formatCurrency(plan.price)}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					<Grid item xs={12}>
						<Alert severity="info">Quantidade fixa: 1 inscrição</Alert>
					</Grid>
				</Grid>
			);
		}

		if (activeStep === 2) {
			return (
				<Grid container spacing={2}>
					<Grid item xs={12} sm={events?.isInstallments ? 6 : 12}>
						<FormControl fullWidth>
							<InputLabel id="pf-payment-form-label">Forma de Pagamento</InputLabel>
							<Select
								labelId="pf-payment-form-label"
								value={pfData.payment_form}
								label="Forma de Pagamento"
								onChange={e => setPfData(prev => ({ ...prev, payment_form: e.target.value, installments: 1 }))}
							>
								<MenuItem value="">Selecione</MenuItem>
								{currentPaymentOptions.map(option => (
									<MenuItem key={option} value={option}>{option}</MenuItem>
								))}
							</Select>
						</FormControl>
					</Grid>
					{events?.isInstallments && (
						<Grid item xs={12} sm={6}>
							<FormControl fullWidth>
								<InputLabel id="pf-installments-label">Parcelamento</InputLabel>
								<Select
									labelId="pf-installments-label"
									value={pfData.installments}
									label="Parcelamento"
									onChange={e => setPfData(prev => ({ ...prev, installments: Number(e.target.value) }))}
									disabled={!pfData.payment_form || pfData.payment_form === 'Gratuito'}
								>
									{installmentOptions.map(option => (
										<MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
									))}
								</Select>
							</FormControl>
						</Grid>
					)}
					<Grid item xs={12}>
						<Alert severity="info">Valor total: {formatCurrency(pfTotal)}</Alert>
					</Grid>
				</Grid>
			);
		}

		return renderReview();
	}

	function renderReview() {
		const plan = payerType === 'INSTITUICAO' ? selectedCompanyPlan : selectedPfPlan;
		const paymentForm = payerType === 'INSTITUICAO' ? companyData.payment_form : pfData.payment_form;
		const installments = payerType === 'INSTITUICAO' ? companyData.installments : pfData.installments;
		const total = payerType === 'INSTITUICAO' ? companyTotal : pfTotal;
		const quantity = payerType === 'INSTITUICAO' ? companyData.quantity : 1;

		return (
			<Box>
				<Typography variant="h6" sx={{ mb: 2 }}>Revisão da Inscrição</Typography>
				<Grid container spacing={2}>
					{payerType === 'INSTITUICAO' ? (
						<>
							<Grid item xs={12}><Typography variant="subtitle1">Dados da Instituição</Typography></Grid>
							<Grid item xs={12} sm={6}><TextField label="Razão Social" fullWidth value={companyData.social_reason} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="CNPJ" fullWidth value={companyData.document} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="Telefone" fullWidth value={companyData.phone} disabled /></Grid>
							<Grid item xs={12} sm={6}><TextField label="E-mail" fullWidth value={companyData.email} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="CEP" fullWidth value={companyData.cep} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="Cidade/UF" fullWidth value={`${companyData.city} - ${companyData.state}`} disabled /></Grid>
							<Grid item xs={12}><TextField label="Endereço" fullWidth value={`${companyData.address}, ${companyData.number} - ${companyData.district}`} disabled /></Grid>
							<Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
							<Grid item xs={12}><Typography variant="subtitle1">Inscritos</Typography></Grid>
							{subscribers.map((sub, index) => (
								<Grid item xs={12} key={`review-sub-${index + 1}`}>
									<Paper variant="outlined" sx={{ p: 1.5 }}>
										<Typography variant="body2">
											{index + 1}. {sub.name} | {sub.document} | {sub.email} | {sub.whatsapp}
										</Typography>
									</Paper>
								</Grid>
							))}
						</>
					) : (
						<>
							<Grid item xs={12}><Typography variant="subtitle1">Dados do Inscrito</Typography></Grid>
							<Grid item xs={12} sm={6}><TextField label="Nome" fullWidth value={pfData.name} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="CPF" fullWidth value={pfData.document} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="Nascimento" fullWidth value={pfData.birth} disabled /></Grid>
							<Grid item xs={12} sm={4}><TextField label="Gênero" fullWidth value={pfData.gender} disabled /></Grid>
							<Grid item xs={12} sm={4}><TextField label="WhatsApp" fullWidth value={pfData.whatsapp} disabled /></Grid>
							<Grid item xs={12} sm={4}><TextField label="E-mail" fullWidth value={pfData.email} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="CEP" fullWidth value={pfData.cep} disabled /></Grid>
							<Grid item xs={12} sm={6}><TextField label="Endereço" fullWidth value={`${pfData.street}, ${pfData.number}`} disabled /></Grid>
							<Grid item xs={12} sm={3}><TextField label="Bairro" fullWidth value={pfData.neighborhood} disabled /></Grid>
							<Grid item xs={12} sm={4}><TextField label="Cidade" fullWidth value={pfData.city} disabled /></Grid>
							<Grid item xs={12} sm={2}><TextField label="UF" fullWidth value={pfData.state} disabled /></Grid>
						</>
					)}

					<Grid item xs={12}><Divider sx={{ my: 1 }} /></Grid>
					<Grid item xs={12}><Typography variant="subtitle1">Pagamento</Typography></Grid>
					<Grid item xs={12} sm={6}><TextField label="Tipo de Inscrição" fullWidth value={plan ? `${plan.type} - ${formatCurrency(plan.price)}` : ''} disabled /></Grid>
					<Grid item xs={12} sm={2}><TextField label="Quantidade" fullWidth value={quantity} disabled /></Grid>
					<Grid item xs={12} sm={4}><TextField label="Forma de Pagamento" fullWidth value={paymentForm} disabled /></Grid>
					<Grid item xs={12} sm={4}><TextField label="Parcelamento" fullWidth value={`${installments}x de ${formatCurrency(total / Number(installments || 1))}`} disabled /></Grid>
					<Grid item xs={12} sm={4}><TextField label="Valor Total" fullWidth value={formatCurrency(total)} disabled /></Grid>
				</Grid>

				{needsPolicyAcceptance && (
					<Box sx={{ mt: 2 }}>
						<FormControlLabel
							control={<Checkbox checked={acceptPolicy} onChange={e => setAcceptPolicy(e.target.checked)} />}
							label={
								<span>
									Li e aceito a{' '}
									<a href={`${basePath}/privacy-policy`} target="_blank" rel="noreferrer">Política de Privacidade</a>
									{manager?.fantasy === 'AGIP' ? (
										<>
											{' '}e a{' '}
											<a href={`${basePath}/subscription-policy`} target="_blank" rel="noreferrer">Política de Inscrições</a>
										</>
									) : null}
									.
								</span>
							}
						/>
					</Box>
				)}
			</Box>
		);
	}

	if (eventLoading) {
		return (
			<Box sx={{ width: '100vw', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
				<CircularProgress size={40} />
			</Box>
		);
	}

	return (
		<Box sx={{ width: '100vw', minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
			<AppNav />
			<Dialog open={openBoletoModal} onClose={() => setOpenBoletoModal(false)} maxWidth="md" fullWidth>
				<DialogTitle>Boletos Gerados</DialogTitle>
				<DialogContent dividers>
					{boletoGroups.map((group, index) => (
						<Accordion key={`${group.name}-${index}`} defaultExpanded={index === 0}>
							<AccordionSummary expandIcon={<ExpandMoreIcon />}>
								<Typography fontWeight={600}>{group.name}</Typography>
							</AccordionSummary>
							<AccordionDetails>
								<Grid container spacing={1}>
									{group.boletos.map((boleto, boletoIndex) => (
										<Grid item xs={12} key={`${group.name}-${boleto.parcel}-${boletoIndex}`}>
											<Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
												<Box>
													<Typography variant="body2">
														Parcela {boleto.parcel} - Boleto #{boleto.externalId}
													</Typography>
													<Typography variant="caption" color="text.secondary">
														{boleto.status} | {formatCurrency(boleto.total)}
													</Typography>
												</Box>
												<IconButton
													color="primary"
													onClick={() => boleto.url && window.open(boleto.url, '_blank')}
													disabled={!boleto.url}
													title="Visualizar boleto"
												>
													<VisibilityIcon />
												</IconButton>
											</Paper>
										</Grid>
									))}
								</Grid>
							</AccordionDetails>
						</Accordion>
					))}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setOpenBoletoModal(false)}>Fechar</Button>
				</DialogActions>
			</Dialog>
			<Paper elevation={3} sx={{ borderRadius: 0, p: { xs: 1.5, sm: 2.5, md: 4 }, 
					width: '100%', height: '100%', my: 8, mx: 'auto', display: 'flex', flexDirection: 'column', justifyContent: 'center'
					}}>
				<Box sx={{ maxWidth: 1240, mx: 'auto',height: '100%' }}>
					<Typography variant="h5" align="center" gutterBottom fontWeight={700} color="#004D91">
						Inscrição para o evento {events?.name && (events.name.length > 80 ? `${events.name.substring(0, 80)}...` : events.name)}
					</Typography>
			
						<Typography variant="subtitle1" align="center" sx={{ mb: 2 }}>
							Selecione o tipo de pagador e preencha os dados para realizar a inscrição. Se tiver dúvidas, entre em contato conosco.
						</Typography>
					

					<FormControl fullWidth sx={{ mb: 3 }}>
						<InputLabel id="payer-type-label">Tipo de Pagador</InputLabel>
						<Select
							labelId="payer-type-label"
							value={payerType}
							label="Tipo de Pagador"
							onChange={e => resetToType(e.target.value)}
						>
							<MenuItem value="INSTITUICAO">Instituição</MenuItem>
							<MenuItem value="PF">Pessoa Física</MenuItem>
						</Select>
					</FormControl>

					<Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
						{currentSteps.map(step => (
							<Step key={step}>
								<StepLabel>{step}</StepLabel>
							</Step>
						))}
					</Stepper>

					{(activeStep === 0 || activeStep === 1) && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, mb: 3, flexWrap: 'wrap', p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
							<Button
								variant="contained"
								color="warning"
								onClick={() => handleOpenSecondViaModal('boleto')}
							>
								2ª Via do Boleto
							</Button>
							{
								currentPaymentOptions.includes('Pix') && (
														<Button
								variant="contained"
								color="primary"
								onClick={() => handleOpenSecondViaModal('pix')}
							>
								Continuar processamento Pix
							</Button>
								)
							}
							{manager?.config_path !== 'agip' && (
								<Button
									variant="contained"
									sx={{ backgroundColor: 'rgb(3, 114, 96)' }}
									onClick={handleOpenReceiptModal}
								>
									Comprovante de Inscrição
								</Button>
							)}
						</Box>
					)}

					<Box sx={{ minHeight: 320 }}>
						{payerType === 'INSTITUICAO' ? renderCompanyStepContent() : renderPfStepContent()}
					</Box>

					{stepError && <Typography color="error" sx={{ mt: 2 }}>{stepError}</Typography>}

					<Dialog open={openPixModal} onClose={handleCloseSecondViaModal} maxWidth="sm" fullWidth>
						<DialogTitle>{secondViaMode === 'pix' ? 'Checkout Pix' : '2ª Via do Boleto'}</DialogTitle>
						<DialogContent>
							<Typography variant="body2" sx={{ mb: 2 }}>
								Informe o {payerType === 'INSTITUICAO' ? 'CNPJ' : 'CPF'} para buscar o pagamento:
							</Typography>
							<TextField
								fullWidth
								label={payerType === 'INSTITUICAO' ? 'CNPJ' : 'CPF'}
								value={secondViaDocument}
								onChange={e => setSecondViaDocument(payerType === 'INSTITUICAO' ? maskCNPJ(e.target.value) : maskCPF2(e.target.value))}
								error={Boolean(secondViaError)}
								helperText={secondViaError}
								inputProps={{ maxLength: payerType === 'INSTITUICAO' ? 18 : 14 }}
								autoFocus
								onKeyPress={e => { if (e.key === 'Enter') handleBuscarSecondVia(); }}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseSecondViaModal} disabled={secondViaLoading}>
								Cancelar
							</Button>
							<Button
								onClick={handleBuscarSecondVia}
								variant="contained"
								disabled={secondViaLoading || !secondViaDocument}
							>
								{secondViaLoading ? <CircularProgress size={24} /> : (secondViaMode === 'pix' ? 'Continuar Pagamento Pix' : 'Buscar Boleto')}
							</Button>
						</DialogActions>
					</Dialog>

					<Dialog open={openReceiptModal} onClose={handleCloseReceiptModal} maxWidth="sm" fullWidth>
						<DialogTitle>Comprovante de Inscrição</DialogTitle>
						<DialogContent>
							<Typography variant="body2" sx={{ mb: 2 }}>
								Informe o CPF utilizado na inscrição para consultar o comprovante.
							</Typography>
							<TextField
								fullWidth
								label="CPF"
								value={receiptDocument}
								onChange={e => setReceiptDocument(maskCPF2(e.target.value))}
								error={Boolean(receiptError)}
								helperText={receiptError}
								inputProps={{ maxLength: 14 }}
								autoFocus
								onKeyPress={e => { if (e.key === 'Enter') handleBuscarReceipt(); }}
							/>
						</DialogContent>
						<DialogActions>
							<Button onClick={handleCloseReceiptModal} disabled={receiptLoading}>Cancelar</Button>
							<Button
								onClick={handleBuscarReceipt}
								variant="contained"
								disabled={receiptLoading || !receiptDocument}
							>
								{receiptLoading ? <CircularProgress size={24} /> : 'Buscar Comprovante'}
							</Button>
						</DialogActions>
					</Dialog>

					{success && activeStep === lastStep && (
						<Box sx={{ mt: 2 }}>
							<Alert severity="success" sx={{ mb: 2 }}>
								Inscrição realizada com sucesso! Os dados foram enviados e esta tela permanecerá no passo final.
							</Alert>
							{lastPaymentForm === 'Transação ou Depósito Bancário' && manager && (
								<Box sx={{ mt: 2, p: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 1 }}>
									<Typography variant="subtitle2">Dados para Transação/Depósito:</Typography>
									<Typography variant="body2">{(manager.social_reason || '').toString().toUpperCase()} - {manager.fantasy}</Typography>
									<Typography variant="body2">{(manager.bank || '').toString().toUpperCase()}</Typography>
									<Typography variant="body2">AGÊNCIA {manager.agency} &nbsp; OPERAÇÃO {manager.operation} &nbsp; CONTA {manager.cc}</Typography>
									<Typography variant="body2">CNPJ {manager.document}</Typography>
									<Typography variant="body2">Enviar comprovante para: <a href={`mailto:${manager.email}`}>{manager.email}</a> ou WhatsApp <a href={`https://api.whatsapp.com/send?phone=55${manager.whatsapp}`}>{manager.whatsapp}</a></Typography>
								</Box>
							)}
							<Button variant="outlined" onClick={startNewSubscription} sx={{ mt: 2 }}>
								Nova inscrição
							</Button>
						</Box>
					)}

					<Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
						<Button variant="outlined" disabled={activeStep === 0 || loading} onClick={handleBack}>
							Voltar
						</Button>

						{activeStep < lastStep ? (
							<Button variant="contained" onClick={handleNext} disabled={loading}>
								Próximo
							</Button>
						) : (
							<Button
								color="success"
								variant="contained"
								onClick={async () => {
									const err = validateCurrentStep();
									if (err) {
										setStepError(err);
										return;
									}
									await handleSubmit();
								}}
								disabled={loading || (needsPolicyAcceptance && !acceptPolicy)}
							>
								{loading ? 'Enviando...' : 'Finalizar'}
							</Button>
						)}
					</Box>
				</Box>
			</Paper>
		</Box>
	);
}
